import { observable } from 'mobx';
import type { OrderItemJobTypeKey } from '@this/src/domain/order_item/order_item_job_type';

export type EanHotelCancelCommentType = 'normal' | 'warning' | 'important';

export const eanHotelCancelCommentTypeLabels: Record<EanHotelCancelCommentType, string> = {
  normal: '通常',
  warning: '要確認',
  important: '重要'
};

export type EanHotelCancelChargeSource = 'api_refund' | 'policy' | 'zero';

export type EanHotelCancelInventoryCommentResponseArgs = {
  id: number;
  content: string;
  status: EanHotelCancelCommentType;
  commented_by_name: string;
  commented_at: string;
};

export type EanHotelCancelInventoryCommentArgs = {
  id: number;
  content: string;
  status: EanHotelCancelCommentType;
  commentedByName: string;
  commentedAt: string;
};

const convertEanHotelCancelInventoryCommentResponseToArgs = (
  response: EanHotelCancelInventoryCommentResponseArgs
): EanHotelCancelInventoryCommentArgs => {
  return {
    id: response.id,
    content: response.content,
    status: response.status,
    commentedByName: response.commented_by_name,
    commentedAt: response.commented_at
  };
};

export type EanHotelCancelItemStatusResponseArgs = {
  order_item_id: number;
  trace_id: number;
  job_type: OrderItemJobTypeKey | null;
  status: number;
};

export type EanHotelCancelItemStatusArgs = {
  orderItemId: number;
  traceId: number;
  jobType: OrderItemJobTypeKey | null;
  status: number;
};

const convertEanHotelCancelItemStatusResponseToArgs = (
  response: EanHotelCancelItemStatusResponseArgs
): EanHotelCancelItemStatusArgs => {
  return {
    orderItemId: response.order_item_id,
    traceId: response.trace_id,
    jobType: response.job_type,
    status: response.status
  };
};

export type EanHotelCancelAmountsResponseArgs = {
  order_item: {
    hotel_price: number | null;
    cancel_charge: number | null;
    total: number;
  };
  ean: {
    amount_before: number;
    amount_after_total: number;
  };
};

export type EanHotelCancelAmountsArgs = {
  orderItem: {
    hotelPrice: number | null;
    cancelCharge: number | null;
    total: number;
  };
  ean: {
    amountBefore: number;
    amountAfterTotal: number;
  };
};

const convertEanHotelCancelAmountsResponseToArgs = (
  response: EanHotelCancelAmountsResponseArgs
): EanHotelCancelAmountsArgs => {
  return {
    orderItem: {
      hotelPrice: response.order_item.hotel_price,
      cancelCharge: response.order_item.cancel_charge,
      total: response.order_item.total
    },
    ean: {
      amountBefore: response.ean.amount_before,
      amountAfterTotal: response.ean.amount_after_total
    }
  };
};

export type EanHotelCancelInventoryItemResponseArgs = {
  id: number;
  trip_id: number;
  itinerary_ids: string[];
  original_item: EanHotelCancelItemStatusResponseArgs | null;
  cancel_charge_item: EanHotelCancelItemStatusResponseArgs | null;
  api_canceled: boolean;
  api_room_statuses: string[];
  hotel_name: string | null;
  checkin_date: string | null;
  checkout_date: string | null;
  amounts: EanHotelCancelAmountsResponseArgs;
  cancel_charge_source: EanHotelCancelChargeSource | null;
  billed_at: string | null;
  requested_at: string;
  api_canceled_at: string | null;
  canceled_by_name: string | null;
  is_inventoried: boolean;
  inventoried_by: string | null;
  inventoried_at: string | null;
  last_error_message: string | null;
  comments: EanHotelCancelInventoryCommentResponseArgs[];
  api_uncanceled: boolean;
  amount_mismatched: boolean;
  same_room_type_ambiguous: boolean;
  bulk_inventoriable: boolean;
};

export type EanHotelCancelInventoryItemArgs = {
  id: number;
  tripId: number;
  itineraryIds: string[];
  originalItem: EanHotelCancelItemStatusArgs | null;
  cancelChargeItem: EanHotelCancelItemStatusArgs | null;
  apiCanceled: boolean;
  apiRoomStatuses: string[];
  hotelName: string | null;
  checkinDate: string | null;
  checkoutDate: string | null;
  amounts: EanHotelCancelAmountsArgs;
  cancelChargeSource: EanHotelCancelChargeSource | null;
  billedAt: string | null;
  requestedAt: string;
  apiCanceledAt: string | null;
  canceledByName: string | null;
  isInventoried: boolean;
  inventoriedBy: string | null;
  inventoriedAt: string | null;
  lastErrorMessage: string | null;
  comments: EanHotelCancelInventoryCommentArgs[];
  apiUncanceled: boolean;
  amountMismatched: boolean;
  sameRoomTypeAmbiguous: boolean;
  bulkInventoriable: boolean;
};

const convertEanHotelCancelInventoryItemResponseToArgs = (
  response: EanHotelCancelInventoryItemResponseArgs
): EanHotelCancelInventoryItemArgs => {
  return {
    id: response.id,
    tripId: response.trip_id,
    itineraryIds: response.itinerary_ids,
    originalItem: response.original_item && convertEanHotelCancelItemStatusResponseToArgs(response.original_item),
    cancelChargeItem:
      response.cancel_charge_item && convertEanHotelCancelItemStatusResponseToArgs(response.cancel_charge_item),
    apiCanceled: response.api_canceled,
    apiRoomStatuses: response.api_room_statuses,
    hotelName: response.hotel_name,
    checkinDate: response.checkin_date,
    checkoutDate: response.checkout_date,
    amounts: convertEanHotelCancelAmountsResponseToArgs(response.amounts),
    cancelChargeSource: response.cancel_charge_source,
    billedAt: response.billed_at,
    requestedAt: response.requested_at,
    apiCanceledAt: response.api_canceled_at,
    canceledByName: response.canceled_by_name,
    isInventoried: response.is_inventoried,
    inventoriedBy: response.inventoried_by,
    inventoriedAt: response.inventoried_at,
    lastErrorMessage: response.last_error_message,
    comments: response.comments.map(convertEanHotelCancelInventoryCommentResponseToArgs),
    apiUncanceled: response.api_uncanceled,
    amountMismatched: response.amount_mismatched,
    sameRoomTypeAmbiguous: response.same_room_type_ambiguous,
    bulkInventoriable: response.bulk_inventoriable
  };
};

export default class EanHotelCancelInventoryItem {
  @observable readonly id: number;
  @observable readonly tripId: number;
  @observable readonly itineraryIds: string[];
  @observable readonly originalItem: EanHotelCancelItemStatusArgs | null;
  @observable readonly cancelChargeItem: EanHotelCancelItemStatusArgs | null;
  @observable readonly apiCanceled: boolean;
  @observable readonly apiRoomStatuses: string[];
  @observable readonly hotelName: string | null;
  @observable readonly checkinDate: string | null;
  @observable readonly checkoutDate: string | null;
  @observable readonly amounts: EanHotelCancelAmountsArgs;
  @observable readonly cancelChargeSource: EanHotelCancelChargeSource | null;
  @observable readonly billedAt: string | null;
  @observable readonly requestedAt: string;
  @observable readonly apiCanceledAt: string | null;
  @observable readonly canceledByName: string | null;
  @observable readonly isInventoried: boolean;
  @observable readonly inventoriedBy: string | null;
  @observable readonly inventoriedAt: string | null;
  @observable readonly lastErrorMessage: string | null;
  @observable readonly comments: EanHotelCancelInventoryCommentArgs[];
  @observable readonly apiUncanceled: boolean;
  @observable readonly amountMismatched: boolean;
  @observable readonly sameRoomTypeAmbiguous: boolean;
  // 一括棚卸し可否はサーバー側のDomainサービスが正。FEで再計算すると判定差分が生まれる
  @observable readonly bulkInventoriable: boolean;

  constructor(args: EanHotelCancelInventoryItemArgs) {
    this.id = args.id;
    this.tripId = args.tripId;
    this.itineraryIds = args.itineraryIds;
    this.originalItem = args.originalItem;
    this.cancelChargeItem = args.cancelChargeItem;
    this.apiCanceled = args.apiCanceled;
    this.apiRoomStatuses = args.apiRoomStatuses;
    this.hotelName = args.hotelName;
    this.checkinDate = args.checkinDate;
    this.checkoutDate = args.checkoutDate;
    this.amounts = args.amounts;
    this.cancelChargeSource = args.cancelChargeSource;
    this.billedAt = args.billedAt;
    this.requestedAt = args.requestedAt;
    this.apiCanceledAt = args.apiCanceledAt;
    this.canceledByName = args.canceledByName;
    this.isInventoried = args.isInventoried;
    this.inventoriedBy = args.inventoriedBy;
    this.inventoriedAt = args.inventoriedAt;
    this.lastErrorMessage = args.lastErrorMessage;
    this.comments = args.comments;
    this.apiUncanceled = args.apiUncanceled;
    this.amountMismatched = args.amountMismatched;
    this.sameRoomTypeAmbiguous = args.sameRoomTypeAmbiguous;
    this.bulkInventoriable = args.bulkInventoriable;
  }

  static fromResponse(response: EanHotelCancelInventoryItemResponseArgs): EanHotelCancelInventoryItem {
    return new EanHotelCancelInventoryItem(convertEanHotelCancelInventoryItemResponseToArgs(response));
  }

  // EANキャンセルは自動実行のみでAPIのcanceled_by_nameは常にNULL。未実行の行は実行者なし
  get canceledByLabel(): string | null {
    if (this.canceledByName) return this.canceledByName;
    return this.apiCanceledAt ? 'システム' : null;
  }

  // API失敗だけでなく、API成功後に会計計上を停止したケースもlastErrorMessageで表面化する
  get needsAttention(): boolean {
    return this.apiUncanceled || !!this.lastErrorMessage || this.sameRoomTypeAmbiguous;
  }

  // policy算出行は「キャンセル料=計上額」が恒真式で突合の意味がないため、一覧上で取得元を明示する
  get cancelChargeSourceLabel(): string {
    return this.cancelChargeSource === 'policy' ? 'ポリシー算出' : '';
  }

  // 同タイプ複数室は室を特定できず先頭室の返金額が全室に書かれ、合計同士の突合では検知できないため目視を促す
  get sameRoomTypeAmbiguousLabel(): string {
    return this.sameRoomTypeAmbiguous ? '同タイプ複数室（返金額要確認）' : '';
  }
}
